/**
 * Guardia contro la navigazione accidentale del popup SIP vivo (GAIA-2674).
 *
 * `window.open(url, POPUP_WINDOW_NAME)` su una finestra con quel nome GIÀ APERTA
 * non ne apre una nuova: la NAVIGA. Se il popup sta gestendo una chiamata, quella
 * navigazione distrugge la sessione WebRTC — la chiamata cade, al reload compare il
 * banner "interrotta dal refresh" e, poiché `SessionState.Terminated` non scatta
 * mai, `createCallLog` non parte: la chiamata non appare nei KPI.
 *
 * L'heartbeat (2s, timeout 5s) NON basta a decidere: il popup è una finestra in
 * background e quando il browser ne throttla i timer smette di batterlo pur essendo
 * vivo e in chiamata → falso negativo → navigazione. Il Web Lock esclusivo
 * `gaia-sip-owner`, invece, è tenuto dal popup vivo, non è soggetto al throttling
 * dei timer e viene rilasciato dal browser quando la finestra muore: è l'unica
 * autorità su "esiste già un popup".
 */

export type PopupOpenAction = "open" | "focus";

export interface PopupOpenDecision {
  /** `open` = window.open(url, name) lecito; `focus` = porta in primo piano, MAI navigare. */
  action: PopupOpenAction;
  /**
   * True quando il solo heartbeat avrebbe fatto navigare un popup vivo: è il
   * near-miss da telemetrare, la firma esatta di GAIA-2674 in produzione.
   */
  preventedNavigation: boolean;
}

export function decidePopupOpen(o: {
  /** Heartbeat recente ricevuto dal popup: affidabile solo se true. */
  isPopupAlive: boolean;
  /** Esito di `isSipOwnerPresent()`: true/false, oppure null se Web Locks non è disponibile. */
  isOwnerLockHeld: boolean | null;
}): PopupOpenDecision {
  const lockHeld = o.isOwnerLockHeld === true;

  if (lockHeld && !o.isPopupAlive) {
    return { action: "focus", preventedNavigation: true };
  }
  if (lockHeld || o.isPopupAlive) {
    return { action: "focus", preventedNavigation: false };
  }
  return { action: "open", preventedNavigation: false };
}
